import {useEffect, useState} from 'react';
import {ArrivalConfirmation} from '../confirmation/ArrivalConfirmation';
import {Button} from '../../form/button/Button';
import {Input} from 'src/components/form/input/Input';
import './BookingModal.scss';
import {useTranslation} from 'react-i18next';
import {performBookingArrival, postValidatePnr} from 'src/services/api/api';
import {useParams} from 'react-router-dom';
import {ArrivalNotFound} from '../confirmation/ArrivalNotFound';
import {Spinner} from 'src/components/layout/loader/Spinner';
import {getSystemCountry} from 'src/services/i18n/locale';
import {getIdentityImpl} from 'src/services/i18n/identity';

type ArrivalStatus = 'confirmed' | 'not_found' | 'getting_info' | 'loading';
interface BookingModalProps {
    onClose?: VoidFunction;
}

const identityImpl = getIdentityImpl();

async function validateIdentifier(identifier: string): Promise<boolean> {
    //if (identifier.length !== identityImpl.maxLength) return false;
    if (!identityImpl.isNumberComplete(identifier)) return false;
    try {
        await postValidatePnr(identifier); // ignore result, just non-error is okay
        return true;
    } catch {
        return false;
    }
}

function numpadContents() {
    const locale = getSystemCountry();
    switch (locale) {
        case 'sv':
            return ['7', '8', '9', '4', '5', '6', '1', '2', '3', 'c', '0', '<'];
        default:
            return [];
    }
}

export function BookingModal({onClose}: BookingModalProps) {
    const {clinicId} = useParams<{clinicId: string}>();
    const clinicIdNum = parseInt(clinicId || '', 10);
    const {t} = useTranslation();
    const [status, setStatus] = useState<ArrivalStatus>('getting_info');
    const [identifier, setIdentifier] = useState('');
    const [nextEnabled, setNextEnabled] = useState(false);

    const systemCountry = getSystemCountry();
    const identifierInputDefault = t(`people_select.people_form.identity_placeholder.${systemCountry}`);

    function onConfirmIdentity() {
        setStatus('loading');

        performBookingArrival(clinicIdNum, identifier, null)
            .then((res) => {
                setStatus('confirmed');
            })
            .catch((e) => {
                setStatus('not_found');
            });
    }

    function upateIdentity(id: string) {
        if (id.length > identityImpl.maxLength()) return;
        setIdentifier(id);
    }

    function addInput(item: string) {
        const locale = getSystemCountry();
        if (locale === 'sv') {
            // Clear input
            if (item === 'c') {
                upateIdentity('');
                return;
            }

            // Remove last digit
            if (item === '<') {
                upateIdentity(identifier.slice(0, -1));
                return;
            }
        }

        // Append item to input field contents, if any
        const id = identifier === identifierInputDefault ? item : identifier + item;
        upateIdentity(id);
    }

    useEffect(() => {
        validateIdentifier(identifier).then((valid) => {
            setNextEnabled(valid);
        });
    }, [identifier]);

    return (
        <div className='identification-component'>
            {status === 'getting_info' && (
                <div>
                    <div className='identification-component__inputDiv'>
                        <Input
                            onChange={(e) => upateIdentity(e)}
                            type='text'
                            id='booking-identity'
                            value={identifier}
                            placeholder={identifierInputDefault}
                            label={t(`people_select.people_form.identity_label.${systemCountry}`)}
                            isValid={identifier.length === 0 || nextEnabled}
                            focusOnMount={true}
                        />
                        <Button
                            className='identification-component__confirm'
                            onClick={() => onConfirmIdentity()}
                            disabled={!nextEnabled}
                        >
                            {t('arrival.next')}
                        </Button>
                    </div>
                    <div className='identification-component__numpad-container'>
                        {numpadContents().map((item) => (
                            <button
                                className='identification-component__numpad-input'
                                value={item}
                                key={item}
                                onClick={() => addInput(item)}
                            >
                                {item}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {status === 'confirmed' && <ArrivalConfirmation onClose={onClose} />}

            {status === 'not_found' && <ArrivalNotFound onClose={onClose} />}

            {status === 'loading' && (
                <div style={{width: '150px', height: '150px', margin: 'auto'}}>
                    <Spinner />
                </div>
            )}
        </div>
    );
}
